import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';

interface TableGridInputProps {
  /** Fixed column headers, from the property's `columns` config. */
  columns: string[];
  invalid: boolean;
  value: string;
  onChange: (value: string) => void;
}

type TableRow = Record<string, string>;

/** A manually-entered table's value is its rows serialized as JSON; unusable/blank values are just no rows. */
function parseRows(value: string): TableRow[] {
  if (!value.trim()) return [];

  try {
    const parsed: unknown = JSON.parse(value);
    return Array.isArray(parsed) ? (parsed as TableRow[]) : [];
  } catch {
    return [];
  }
}

/** A grid of the property's configured columns; the consultant fills in rows directly instead of uploading a file. */
export function TableGridInput({ columns, invalid, value, onChange }: TableGridInputProps) {
  const { t } = useTranslation();
  const rows = parseRows(value);

  // The author hasn't set Columns yet, so there is nothing to build a grid around.
  if (columns.length === 0) {
    return <p className="mt-1 text-sm text-muted-foreground">{t('table.grid.noColumns')}</p>;
  }

  const setRows = (next: TableRow[]) => onChange(JSON.stringify(next));
  const setCell = (index: number, column: string, cellValue: string) =>
    setRows(rows.map((row, i) => (i === index ? { ...row, [column]: cellValue } : row)));
  const addRow = () => setRows([...rows, Object.fromEntries(columns.map((column) => [column, '']))]);
  const removeRow = (index: number) => setRows(rows.filter((_, i) => i !== index));

  return (
    <div className={`mt-1 overflow-auto rounded border ${invalid ? 'border-destructive' : 'border-input'}`}>
      <table className="min-w-full divide-y divide-border text-sm">
        <thead className="bg-muted">
          <tr>
            {columns.map((column) => (
              <th key={column} scope="col" className="px-3 py-2 text-left font-semibold text-foreground">
                {column}
              </th>
            ))}
            <th scope="col" className="px-3 py-2" />
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {rows.length === 0 ? (
            <tr>
              <td colSpan={columns.length + 1} className="px-3 py-2 text-muted-foreground">
                {t('table.grid.empty')}
              </td>
            </tr>
          ) : (
            rows.map((row, index) => (
              // Rows carry no id, and reordering is out of scope, so the index is stable.
              <tr key={index}>
                {columns.map((column) => (
                  <td key={column} className="px-2 py-1.5">
                    <input
                      type="text"
                      aria-label={`${column} row ${index + 1}`}
                      value={row[column] ?? ''}
                      onChange={(event) => setCell(index, column, event.target.value)}
                      className="w-full rounded-sm border border-input bg-surface px-2 py-1 text-sm outline-none transition focus:border-ring focus:ring-2 focus:ring-ring/22"
                    />
                  </td>
                ))}
                <td className="px-2 py-1.5 text-right">
                  <button
                    type="button"
                    aria-label={t('table.grid.removeRow', { index: index + 1 })}
                    onClick={() => removeRow(index)}
                    className="rounded p-1 text-muted-foreground transition hover:text-foreground"
                  >
                    <TrashIcon className="size-4" aria-hidden="true" />
                  </button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>

      <div className="border-t border-border bg-surface p-2">
        <button
          type="button"
          onClick={addRow}
          className="inline-flex items-center gap-1.5 rounded border border-input bg-surface px-2.5 py-1.5 text-xs font-semibold text-foreground transition hover:bg-muted"
        >
          <PlusIcon className="size-4" aria-hidden="true" />
          {t('table.grid.addRow')}
        </button>
      </div>
    </div>
  );
}
